import { doc, getDoc, onSnapshot, collection, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import tabCoordinator from './tabCoordinator';
import { effectiveOfflineEnd } from '../utils/offlinePeriod';

class AppDataManager {
  constructor() {
    this.isActive = false;
    this.listeners = new Map();
    this.updateCallbacks = new Map(); // Für jede Datenquelle separate Callbacks
    
    // Data state
    this.data = {
      drivers: [],
      vehicles: [],
      rides: [],
      shifts: { drivers: {} },
      customers: [],
      activities: [], // Konsolidierte Activities für WaitTimeManager
      notifications: []
    };

    this.notificationsUserId = null;
    this.currentUser = null;

    // Ride-IDs, für die der "Annahme"-Ton bereits gespielt wurde.
    // Verhindert Doppel-Sound bei mehrfachen Firestore-modified-Events
    // (z.B. lokales Snapshot + Server-Snapshot bei serverTimestamp()).
    this._dispatcherAcceptedSoundPlayed = new Set();

    // Binding
    this.setupDriversListener = this.setupDriversListener.bind(this);
    this.setupVehiclesListener = this.setupVehiclesListener.bind(this);
    this.setupRidesListener = this.setupRidesListener.bind(this);
    this.setupShiftsListener = this.setupShiftsListener.bind(this);
    this.setupCustomersListener = this.setupCustomersListener.bind(this);
    this.setupNotificationsListener = this.setupNotificationsListener.bind(this);
    this.calculateActivities = this.calculateActivities.bind(this);
    this.getActiveShift = this.getActiveShift.bind(this);
  }

  setCurrentUser(user) {
    this.currentUser = user || null;
  }

  getOperatorScope() {
    const role = this.currentUser?.role;
    if (role === 'operator_admin' || role === 'operator_staff') {
      return this.currentUser?.operatorId || null;
    }
    return null;
  }

  // Hilfsfunktion für aktive Schicht im neuen Array-System
  getActiveShift(driverData) {
    if (!driverData || !driverData.shifts || !Array.isArray(driverData.shifts)) {
      return null;
    }
    return driverData.shifts.find(shift => shift.isActive) || null;
  }

  // Activities aus allen Datenquellen berechnen (wie im ShiftCalendar)
  calculateActivities() {
    const activities = [];

    // Debug-Logs zum Verständnis des Activity-Problems
    console.log('AppDataManager: === calculateActivities START ===');
    console.log('- Rides verfügbar:', this.data.rides ? this.data.rides.length : 'keine');
    console.log('- Vehicles verfügbar:', this.data.vehicles ? this.data.vehicles.length : 'keine');
    console.log('- Shifts verfügbar:', this.data.shifts?.drivers ? Object.keys(this.data.shifts.drivers).length : 'keine');
    
    try {
      // 1. Break Activities aus Shifts
      if (this.data.shifts && this.data.shifts.drivers) {
        Object.entries(this.data.shifts.drivers).forEach(([driverId, driverData]) => {
          const currentShift = this.getActiveShift(driverData);
          if (currentShift && currentShift.breaks && Array.isArray(currentShift.breaks)) {
            currentShift.breaks.forEach((breakItem) => {
              if (breakItem.startTime) {
                const startTime = breakItem.startTime.toDate();
                const endTime = breakItem.endTime 
                  ? breakItem.endTime.toDate() 
                  : new Date(startTime.getTime() + ((breakItem.plannedDuration || 30) * 60000));
                
                const driverVehicle = this.data.vehicles.find(v => v.current_driver?.id === driverId);
                
                const now = new Date();
                const calculatedStatus = (() => {
                  if (breakItem.endTime) {
                    return endTime <= now ? 'completed' : 'active';
                  } else {
                    return endTime <= now ? 'completed' : 'active';
                  }
                })();
                
                activities.push({
                  id: `break-${driverId}-${startTime.getTime()}`,
                  type: 'break',
                  vehicleId: driverVehicle ? driverVehicle.id : `virtual-${driverId}`,
                  startTime,
                  endTime,
                  endTimePlanned: endTime,
                  destination: breakItem.type || 'Pause',
                  driverId,
                  status: calculatedStatus,
                  isDelayed: false,
                  plannedDuration: breakItem.plannedDuration
                });
              }
            });
          }
        });
      }

      // 2. Offline Activities aus Shifts
      if (this.data.shifts && this.data.shifts.drivers) {
        Object.entries(this.data.shifts.drivers).forEach(([driverId, driverData]) => {
          let shiftsArray = [];
          
          if (driverData.shifts && Array.isArray(driverData.shifts)) {
            shiftsArray = driverData.shifts;
          } else if (driverData.shiftStart) {
            shiftsArray = [{
              shiftNumber: 1,
              shiftStart: driverData.shiftStart,
              shiftEnd: driverData.shiftEnd,
              isActive: driverData.isActive || false,
              breaks: driverData.breaks || [],
              offlinePeriods: []
            }];
          }
          
          shiftsArray.forEach((shift) => {
            if (shift.offlinePeriods && Array.isArray(shift.offlinePeriods)) {
              shift.offlinePeriods.forEach((offlinePeriod) => {
                if (offlinePeriod.offlineStart) {
                  const startTime = offlinePeriod.offlineStart.toDate();
                  const { endTime, isOpen } = effectiveOfflineEnd({
                    offlineStart: startTime,
                    offlineEnd: offlinePeriod.offlineEnd?.toDate?.() || null,
                    shiftStart: shift.shiftStart?.toDate?.() || null,
                  });
                  
                  const driverVehicle = this.data.vehicles.find(v => v.current_driver?.id === driverId);
                  const calculatedStatus = isOpen ? 'active' : 'completed';
                  
                  activities.push({
                    id: `offline-${driverId}-${startTime.getTime()}`,
                    type: 'offline',
                    vehicleId: driverVehicle ? driverVehicle.id : `virtual-${driverId}`,
                    startTime,
                    endTime,
                    endTimePlanned: endTime,
                    destination: offlinePeriod.reason || 'Offline',
                    driverId,
                    status: calculatedStatus,
                    isDelayed: false
                  });
                }
              });
            }
          });
        });
      }

      // 3. Ride Activities - alle relevanten Status berücksichtigen (wie ShiftCalendar)
      if (this.data.rides && Array.isArray(this.data.rides)) {
        console.log(`AppDataManager: Verarbeite ${this.data.rides.length} Rides...`);
        
        this.data.rides.forEach((ride) => {
          console.log(`- Prüfe Ride ${ride.id}: Status=${ride.status}, driverId=${ride.driverId}`);
          
          // Filtere nur Fahrten mit Fahrer und relevanten Status (nicht nur acceptedTimestamp)
          const blockingStatuses = ['pre_accepted', 'accepted', 'arrived', 'in_progress'];
          if (ride.driverId && blockingStatuses.includes(ride.status)) {
            console.log(`  ✓ Ride ${ride.id} hat blockingStatus`);
            let startTime = null;
            
            // Prüfe verschiedene Zeitquellen (wie in ShiftCalendar und useWaitTimeManager)
            if (ride.acceptedTimestamp) {
              if (ride.acceptedTimestamp.toDate) {
                startTime = ride.acceptedTimestamp.toDate();
              } else if (ride.acceptedTimestamp.seconds) {
                startTime = new Date(ride.acceptedTimestamp.seconds * 1000);
              } else {
                startTime = new Date(ride.acceptedTimestamp);
              }
            } else if (ride.scheduledTime) {
              if (ride.scheduledTime.toDate) {
                startTime = ride.scheduledTime.toDate();
              } else if (ride.scheduledTime.seconds) {
                startTime = new Date(ride.scheduledTime.seconds * 1000);
              } else {
                startTime = new Date(ride.scheduledTime);
              }
            } else if (ride.timestamp) {
              if (ride.timestamp.toDate) {
                startTime = ride.timestamp.toDate();
              } else if (ride.timestamp.seconds) {
                startTime = new Date(ride.timestamp.seconds * 1000);
              } else {
                startTime = new Date(ride.timestamp);
              }
            }
            
            let endTime = null;
            if (ride.completedTimestamp) {
              if (ride.completedTimestamp.toDate) {
                endTime = ride.completedTimestamp.toDate();
              } else if (ride.completedTimestamp.seconds) {
                endTime = new Date(ride.completedTimestamp.seconds * 1000);
              } else {
                endTime = new Date(ride.completedTimestamp);
              }
            } else if (startTime && ride.estimatedTimeMinutes) {
              endTime = new Date(startTime.getTime() + (ride.estimatedTimeMinutes * 60000));
            }
            
            console.log(`  - startTime: ${startTime}`);
            console.log(`  - endTime: ${endTime}`);
            console.log(`  - estimatedTimeMinutes: ${ride.estimatedTimeMinutes}`);
            
            if (startTime && endTime) {
              console.log(`  ✓ Activity wird erstellt für Ride ${ride.id}`);
              const driverVehicle = this.data.vehicles.find(v => v.current_driver?.id === ride.driverId);
              
              // Berechne Anfahrtszeit für pre_accepted Status (wie in ShiftCalendar)
              const needsApproachTime = ride.status === 'pre_accepted';
              const approachTimeMinutes = 10;
              let approachStartTime = null;
              let mainStartTime = startTime;
              let effectiveStartTime = startTime;
              
              if (needsApproachTime) {
                approachStartTime = new Date(startTime.getTime() - (approachTimeMinutes * 60000));
                effectiveStartTime = approachStartTime; // Activity beginnt mit Anfahrt
              }
              
              activities.push({
                id: ride.id || ride.uid,
                type: 'ride',
                vehicleId: driverVehicle ? driverVehicle.id : `virtual-${ride.driverId}`,
                startTime: effectiveStartTime, // Gesamtstart (inklusive Anfahrt)
                endTime,
                destination: ride.destination || 'Unbekannt',
                driverId: ride.driverId,
                status: ride.status,
                customerName: ride.customerName,
                orderType: ride.orderType,
                estimatedTimeMinutes: ride.estimatedTimeMinutes,
                // Neue Eigenschaften für Anfahrtszeit (konsistent mit ShiftCalendar)
                hasApproachTime: needsApproachTime,
                approachStartTime: approachStartTime,
                mainStartTime: mainStartTime,
                approachTimeMinutes: needsApproachTime ? approachTimeMinutes : 0
              });
            } else {
              console.log(`  ✗ Activity NICHT erstellt - fehlende Zeit(en)`);
            }
          } else {
            console.log(`  ✗ Ride übersprungen - kein blockingStatus oder keine driverId`);
          }
        });
      } else {
        console.log('AppDataManager: Keine Rides vorhanden zum Verarbeiten');
      }

      // Korrigiere Timing für Anschlusstouren:
      // Wenn ein Fahrer mehrere aktive Fahrten hat, startet die spätere
      // erst nach Ende der früheren Fahrt (nicht ab acceptedTimestamp).
      const driverActivities = {};
      for (const activity of activities) {
        if (activity.type !== 'ride') continue;
        if (!driverActivities[activity.driverId]) {
          driverActivities[activity.driverId] = [];
        }
        driverActivities[activity.driverId].push(activity);
      }

      for (const [driverId, driverRides] of Object.entries(driverActivities)) {
        if (driverRides.length <= 1) continue;
        // Sortiere nach startTime (früheste zuerst)
        driverRides.sort((a, b) => a.startTime - b.startTime);
        // Kaskadiere: jede Folgefahrt startet nach Ende der vorherigen
        for (let i = 1; i < driverRides.length; i++) {
          const prevRide = driverRides[i - 1];
          const currentRide = driverRides[i];
          if (prevRide.endTime > currentRide.startTime && currentRide.estimatedTimeMinutes) {
            console.log(`🔗 Anschlusstour erkannt: Ride ${currentRide.id} für Fahrer ${driverId} — verschiebe Start von ${currentRide.startTime.toISOString()} auf ${prevRide.endTime.toISOString()}`);
            currentRide.startTime = prevRide.endTime;
            currentRide.endTime = new Date(
              currentRide.startTime.getTime() + (currentRide.estimatedTimeMinutes * 60000)
            );
          }
        }
      }

      // Activities aktualisieren
      this.data.activities = activities;
      this.notifyCallbacks('activities', activities);
      
      console.log(`AppDataManager: === calculateActivities ENDE ===`);
      console.log(`- Erstellte Activities: ${activities.length}`);
      console.log(`- Break Activities: ${activities.filter(a => a.type === 'break').length}`);
      console.log(`- Ride Activities: ${activities.filter(a => a.type === 'ride').length}`);
      console.log(`- Offline Activities: ${activities.filter(a => a.type === 'offline').length}`);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('AppDataManager: Activities aktualisiert:', activities.length);
      }
      
    } catch (error) {
      console.error('AppDataManager: Fehler bei Activities-Berechnung:', error);
    }
  }

  // Drivers Listener (aus users Collection mit isDriver=true)
  async setupDriversListener() {
    try {
      const usersRef = collection(db, 'users');
      const operatorScope = this.getOperatorScope();
      const driversQuery = operatorScope
        ? query(usersRef, where('isDriver', '==', true), where('operatorId', '==', operatorScope))
        : query(usersRef, where('isDriver', '==', true));
      
      const unsubscribe = onSnapshot(driversQuery, (snapshot) => {
        const drivers = [];
        snapshot.forEach((doc) => {
          drivers.push({ id: doc.id, ...doc.data() });
        });
        
        this.data.drivers = drivers;
        this.notifyCallbacks('drivers', drivers);
        
        // Drivers selbst erzeugen keine Activities - calculateActivities() nicht nötig
        // Activities werden nur durch Rides, Shifts und Vehicles beeinflusst
        
        console.log('AppDataManager: Drivers aktualisiert:', drivers.length);
      }, (error) => {
        console.error('AppDataManager: Fehler bei Drivers Listener:', error);
      });
      
      this.listeners.set('drivers', unsubscribe);
      
    } catch (error) {
      console.error('AppDataManager: Fehler beim Setup Drivers Listener:', error);
    }
  }

  // Vehicles Listener (korrekte Collection: Vehicles mit Großbuchstabe)
  async setupVehiclesListener() {
    try {
      const vehiclesRef = collection(db, 'Vehicles');
      const operatorScope = this.getOperatorScope();
      const vehiclesQuery = operatorScope
        ? query(vehiclesRef, where('operatorId', '==', operatorScope))
        : vehiclesRef;
      
      const unsubscribe = onSnapshot(vehiclesQuery, async (snapshot) => {
        const vehicles = [];
        
        // Process each vehicle and load driver data if assigned
        for (const docSnapshot of snapshot.docs) {
          const vehicleData = docSnapshot.data();
          const vehicleId = docSnapshot.id;
          
          // Load current driver information if driverId exists
          let current_driver = null;
          if (vehicleData.driverId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', vehicleData.driverId));
              if (userDoc.exists() && userDoc.data().isDriver) {
                const userData = userDoc.data();
                current_driver = {
                  id: vehicleData.driverId,
                  name: userData.name,
                  email: userData.email,
                  phone: userData.phone || '',
                  isAvailable: userData.isAvailable || false,
                  rating: userData.rating || 0,
                  ratingCount: userData.ratingCount || 0
                };
              }
            } catch (error) {
              console.error(`AppDataManager: Fehler beim Laden Driver ${vehicleData.driverId}:`, error);
            }
          }
          
          vehicles.push({ 
            id: vehicleId, 
            ...vehicleData,
            current_driver 
          });
        }
        
        this.data.vehicles = vehicles;
        this.notifyCallbacks('vehicles', vehicles);
        
        // Recalculate activities when vehicles change
        console.log('AppDataManager: Vehicles Listener triggert calculateActivities()');
        this.calculateActivities();
        
        console.log('AppDataManager: Vehicles aktualisiert:', vehicles.length);
        if (process.env.NODE_ENV === 'development') {
          vehicles.forEach(v => {
            if (v.current_driver) {
              console.log(`- Fahrzeug ${v.id} (Typ ${v.vehicleType}): Fahrer ${v.current_driver.name} (${v.current_driver.id})`);
            }
          });
        }
      }, (error) => {
        console.error('AppDataManager: Fehler bei Vehicles Listener:', error);
      });
      
      this.listeners.set('vehicles', unsubscribe);
      
    } catch (error) {
      console.error('AppDataManager: Fehler beim Setup Vehicles Listener:', error);
    }
  }

  // Lokale Sound-Einstellungen aus localStorage lesen
  getLocalSoundSettings() {
    try {
      const stored = localStorage.getItem('localSettings');
      if (stored) {
        const settings = JSON.parse(stored);
        return {
          soundOnInstantOrder: settings.soundOnInstantOrder !== false,
          soundOnPreorder: settings.soundOnPreorder !== false
        };
      }
    } catch (e) {
      console.warn('Fehler beim Lesen der Sound-Einstellungen:', e);
    }
    return { soundOnInstantOrder: true, soundOnPreorder: true };
  }

  // Zeitstempel einer Fahrt in Millisekunden; null wenn unbekannt.
  static rideTimestampMs(timestamp) {
    try {
      if (!timestamp) return null;
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const ms = date.getTime();
      return Number.isFinite(ms) ? ms : null;
    } catch (e) {
      return null;
    }
  }

  // Sound abspielen für neue Fahrtanfragen.
  // Läuft über den tabCoordinator: Bei mehreren offenen Portal-Fenstern spielt
  // nur eines, und ein nach einem Reconnect nachgelieferter Ereignisstau wird
  // nicht nachträglich vertont (docs/KONZEPT_MEHRFACHE_PORTAL_INSTANZEN.md).
  playRideSound(orderType, rideId, timestamp) {
    const isVorbestellung = orderType === 1;
    const soundSettings = this.getLocalSoundSettings();

    // Prüfe ob Sound für diesen Bestelltyp aktiviert ist.
    // Bewusst vor dem Koordinator: Die Einstellung gilt pro Arbeitsplatz, ein
    // Fenster mit abgeschaltetem Ton darf sie nicht für die anderen übernehmen.
    if (isVorbestellung && !soundSettings.soundOnPreorder) return;
    if (!isVorbestellung && !soundSettings.soundOnInstantOrder) return;

    const soundFile = isVorbestellung ? '/sounds/vorbestellung2.mp3' : '/sounds/new_ride_push.mp3';
    const occurredAtMs = AppDataManager.rideTimestampMs(timestamp);
    tabCoordinator.playSound(
      rideId ? `ride:${rideId}` : '',
      () => {
        const audio = new Audio(soundFile);
        audio.play().catch(e => console.warn('Sound konnte nicht abgespielt werden:', e));
      },
      occurredAtMs !== null ? { occurredAtMs } : {},
    );
  }

  // Rides Listener (rides Collection ist korrekt)
  async setupRidesListener() {
    let initialLoadComplete = false;

    try {
      const ridesRef = collection(db, 'rides');
      const operatorScope = this.getOperatorScope();
      const ridesQuery = operatorScope
        ? query(
            ridesRef,
            where('visibleToOperatorIds', 'array-contains', operatorScope),
            orderBy('timestamp', 'desc')
          )
        : query(ridesRef, orderBy('timestamp', 'desc'));

      const unsubscribe = onSnapshot(ridesQuery, (snapshot) => {
        const rides = [];
        snapshot.forEach((doc) => {
          rides.push({ id: doc.id, ...doc.data() });
        });

        // Sound bei neuen Fahrtanfragen (nicht beim initialen Laden)
        if (initialLoadComplete) {
          let soundPlayed = false; // Pro Snapshot nur einmal Sound abspielen (z.B. bei Daueraufträgen)
          for (const change of snapshot.docChanges()) {
            if (change.type === 'added') {
              const rideData = change.doc.data();
              if (['open', 'scheduled'].includes(rideData.status) && rideData.isPhoneCustomer !== true) {
                if (!soundPlayed) {
                  this.playRideSound(rideData.orderType, change.doc.id, rideData.timestamp);
                  soundPlayed = true;
                }
                // Event für UI-Benachrichtigung
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('new-ride-request', {
                    detail: { rideId: change.doc.id, orderType: rideData.orderType, start: rideData.start }
                  }));
                }
              }
            }
            // Sound wenn Fahrer eine Disponenten-Zuweisung angenommen hat.
            // Pro Ride-ID nur einmal abspielen — Firestore liefert nach einem
            // Update mit serverTimestamp() bzw. Folge-Updates (is_active, ...)
            // mehrere modified-Events mit status='accepted'.
            if (change.type === 'modified') {
              const rideData = change.doc.data();
              const rideId = change.doc.id;
              const isAcceptedByDispatcher = rideData.status === 'accepted' && rideData.assignedByDispatcher === true;
              if (isAcceptedByDispatcher) {
                if (!this._dispatcherAcceptedSoundPlayed.has(rideId)) {
                  this._dispatcherAcceptedSoundPlayed.add(rideId);
                  // Kein Altersfilter: Für die Annahme gibt es keinen
                  // verlässlichen Zeitstempel im Dokument.
                  tabCoordinator.playSound(`ride-accepted:${rideId}`, () => {
                    const audio = new Audio('/sounds/accepted.mp3');
                    audio.play().catch(e => console.warn('Sound konnte nicht abgespielt werden:', e));
                  });
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('dispatcher-ride-accepted', {
                      detail: { rideId, driverName: rideData.driverName, start: rideData.start }
                    }));
                  }
                }
              } else {
                // Status wechselt weg von 'accepted' → Eintrag entfernen,
                // damit eine spätere Wiederzuweisung erneut klingelt.
                this._dispatcherAcceptedSoundPlayed.delete(rideId);
              }
            }
            if (change.type === 'removed') {
              this._dispatcherAcceptedSoundPlayed.delete(change.doc.id);
            }
          }
        } else {
          initialLoadComplete = true;
        }

        this.data.rides = rides;
        this.notifyCallbacks('rides', rides);

        // Recalculate activities when rides change
        console.log('AppDataManager: Rides Listener triggert calculateActivities()');
        this.calculateActivities();

        console.log('AppDataManager: Rides aktualisiert:', rides.length);
      }, (error) => {
        console.error('AppDataManager: Fehler bei Rides Listener:', error);
      });
      
      this.listeners.set('rides', unsubscribe);
      
    } catch (error) {
      console.error('AppDataManager: Fehler beim Setup Rides Listener:', error);
    }
  }

  // Shifts Listener (wie im ShiftCalendar)
  async setupShiftsListener() {
    try {
      const formatDateString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const currentDate = new Date();
      const currentDateString = formatDateString(currentDate);
      const previousDate = new Date(currentDate);
      previousDate.setDate(previousDate.getDate() - 1);
      const previousDateString = formatDateString(previousDate);
      
      console.log('AppDataManager: Setup Shifts Listener für:', currentDateString, 'und', previousDateString);
      
      const currentShiftsDocRef = doc(db, 'shifts', currentDateString);
      const previousShiftsDocRef = doc(db, 'shifts', previousDateString);
      
      let currentShiftsData = null;
      let previousShiftsData = null;
      
      const updateCombinedShiftsData = () => {
        const combinedData = { drivers: {} };
        const operatorScope = this.getOperatorScope();
        
        // Zuerst vorherige Schichten laden (für rollierende Ansicht)
        if (previousShiftsData && previousShiftsData.drivers) {
          Object.entries(previousShiftsData.drivers).forEach(([driverId, driverData]) => {
            if (operatorScope && driverData.operatorId !== operatorScope) return;
            combinedData.drivers[driverId] = { ...driverData, _fromPreviousDay: true };
          });
        }
        
        // Dann aktuelle Schichten (überschreibt bei Konflikten)
        if (currentShiftsData && currentShiftsData.drivers) {
          Object.entries(currentShiftsData.drivers).forEach(([driverId, driverData]) => {
            if (operatorScope && driverData.operatorId !== operatorScope) return;
            combinedData.drivers[driverId] = { ...driverData, _fromCurrentDay: true };
          });
        }
        
        this.data.shifts = combinedData;
        this.notifyCallbacks('shifts', combinedData);
        
        // Recalculate activities when shifts change (important for breaks/offline)
        console.log('AppDataManager: Shifts Listener triggert calculateActivities()');
        this.calculateActivities();
        
        console.log('AppDataManager: Shifts aktualisiert:', Object.keys(combinedData.drivers).length, 'Fahrer');
      };
      
      // Listener für aktuelle Schichten
      const unsubscribeCurrentShifts = onSnapshot(currentShiftsDocRef, (docSnapshot) => {
        currentShiftsData = docSnapshot.exists() ? docSnapshot.data() : null;
        updateCombinedShiftsData();
      });
      
      // Listener für vorherige Schichten
      const unsubscribePreviousShifts = onSnapshot(previousShiftsDocRef, (docSnapshot) => {
        previousShiftsData = docSnapshot.exists() ? docSnapshot.data() : null;
        updateCombinedShiftsData();
      });
      
      // Cleanup-Funktion für beide Listener
      const shiftsCleanup = () => {
        unsubscribeCurrentShifts();
        unsubscribePreviousShifts();
      };
      
      this.listeners.set('shifts', shiftsCleanup);
      
    } catch (error) {
      console.error('AppDataManager: Fehler beim Setup Shifts Listener:', error);
    }
  }

  // Customers Listener (aus users Collection mit isDriver=false oder undefined)
  async setupCustomersListener() {
    try {
      if (this.getOperatorScope()) {
        this.data.customers = [];
        this.notifyCallbacks('customers', []);
        return;
      }

      const usersRef = collection(db, 'users');
      const customersQuery = query(usersRef, where('isDriver', '==', false));
      
      const unsubscribe = onSnapshot(customersQuery, (snapshot) => {
        const customers = [];
        snapshot.forEach((doc) => {
          const userData = doc.data();
          // Nur Kunden ohne isDriver oder mit isDriver=false
          if (!userData.isDriver || userData.isDriver === false) {
            customers.push({ id: doc.id, ...userData });
          }
        });
        
        this.data.customers = customers;
        this.notifyCallbacks('customers', customers);
        
        console.log('AppDataManager: Customers aktualisiert:', customers.length);
      }, (error) => {
        console.warn('AppDataManager: Customers Listener Fehler (möglicherweise keine Berechtigung):', error.message);
        // Setze leere Liste und benachrichtige trotzdem
        this.data.customers = [];
        this.notifyCallbacks('customers', []);
      });
      
      this.listeners.set('customers', unsubscribe);
      
    } catch (error) {
      console.warn('AppDataManager: Setup Customers Listener fehlgeschlagen (möglicherweise keine Berechtigung):', error.message);
      // Setze leere Liste für Customers
      this.data.customers = [];
      this.notifyCallbacks('customers', []);
    }
  }

  // Notifications Listener (Echtzeit-Updates für Benachrichtigungen)
  setupNotificationsListener(userId) {
    // Bestehenden Listener stoppen falls vorhanden
    if (this.listeners.has('notifications')) {
      this.listeners.get('notifications')();
      this.listeners.delete('notifications');
    }

    if (!userId) return;

    this.notificationsUserId = userId;

    try {
      const notificationsRef = collection(db, 'notifications');
      const notificationsQuery = query(
        notificationsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
        const notifications = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          notifications.push({
            id: docSnap.id,
            ...data,
            // Firestore Timestamp zu ISO-String konvertieren
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt
          });
        });

        this.data.notifications = notifications;
        this.notifyCallbacks('notifications', notifications);

        console.log('AppDataManager: Notifications aktualisiert:', notifications.length);
      }, (error) => {
        console.warn('AppDataManager: Notifications Listener Fehler:', error.message);
        this.data.notifications = [];
        this.notifyCallbacks('notifications', []);
      });

      this.listeners.set('notifications', unsubscribe);

    } catch (error) {
      console.warn('AppDataManager: Setup Notifications Listener fehlgeschlagen:', error.message);
      this.data.notifications = [];
      this.notifyCallbacks('notifications', []);
    }
  }

  // Alle Listener starten
  async start(user = null) {
    if (this.isActive) {
      console.warn('AppDataManager: Service bereits aktiv');
      return;
    }
    
    this.isActive = true;
    this.setCurrentUser(user);
    
    try {
      console.log('AppDataManager: Starte alle Data Listener...');
      
      // Alle Listener einzeln starten mit Error-Handling
      const listenerPromises = [
        this.setupDriversListener().catch(err => console.warn('Drivers Listener fehlgeschlagen:', err.message)),
        this.setupVehiclesListener().catch(err => console.warn('Vehicles Listener fehlgeschlagen:', err.message)),
        this.setupRidesListener().catch(err => console.warn('Rides Listener fehlgeschlagen:', err.message)),
        this.setupShiftsListener().catch(err => console.warn('Shifts Listener fehlgeschlagen:', err.message)),
        this.setupCustomersListener().catch(err => console.warn('Customers Listener fehlgeschlagen:', err.message))
      ];
      
      await Promise.allSettled(listenerPromises);
      
      console.log('AppDataManager: Data Listener Setup abgeschlossen (verfügbare Collections geladen)');
      
    } catch (error) {
      console.error('AppDataManager: Kritischer Fehler beim Service-Start:', error);
      // Service trotzdem als aktiv markieren, damit verfügbare Daten verwendet werden können
      this.isActive = true;
    }
  }

  // Service stoppen
  async stop() {
    if (!this.isActive) {
      return;
    }
    
    this.isActive = false;
    
    try {
      // Alle Listener stoppen
      this.listeners.forEach((unsubscribe, dataType) => {
        try {
          unsubscribe();
          console.log(`AppDataManager: ${dataType} Listener gestoppt`);
        } catch (error) {
          console.error(`AppDataManager: Fehler beim Stoppen ${dataType} Listener:`, error);
        }
      });
      
      this.listeners.clear();
      this.updateCallbacks.clear();
      
      console.log('AppDataManager: Service gestoppt');
      
    } catch (error) {
      console.error('AppDataManager: Fehler beim Service-Stopp:', error);
    }
  }

  // Callback für Updates registrieren
  registerCallback(dataType, callback) {
    if (!this.updateCallbacks.has(dataType)) {
      this.updateCallbacks.set(dataType, new Set());
    }
    
    this.updateCallbacks.get(dataType).add(callback);
    
    // Aktuelle Daten sofort senden wenn verfügbar
    if (this.data[dataType]) {
      try {
        callback(this.data[dataType]);
      } catch (error) {
        console.error(`AppDataManager: Fehler bei Callback für ${dataType}:`, error);
      }
    }
    
    // Cleanup-Funktion zurückgeben
    return () => {
      if (this.updateCallbacks.has(dataType)) {
        this.updateCallbacks.get(dataType).delete(callback);
      }
    };
  }

  // Alle Callbacks für einen Datentyp benachrichtigen
  notifyCallbacks(dataType, data) {
    if (this.updateCallbacks.has(dataType)) {
      this.updateCallbacks.get(dataType).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`AppDataManager: Fehler bei Update-Callback für ${dataType}:`, error);
        }
      });
    }
  }

  // Aktuelle Daten abrufen
  getData(dataType) {
    return this.data[dataType] || (dataType === 'shifts' ? { drivers: {} } : []);
  }

  // Alle Daten abrufen
  getAllData() {
    return { ...this.data };
  }

  // Service-Status prüfen
  getStatus() {
    return {
      isActive: this.isActive,
      listenersCount: this.listeners.size,
      callbacksCount: Array.from(this.updateCallbacks.values()).reduce((sum, set) => sum + set.size, 0),
      dataStatus: {
        drivers: this.data.drivers.length,
        vehicles: this.data.vehicles.length,
        rides: this.data.rides.length,
        shifts: this.data.shifts?.drivers ? Object.keys(this.data.shifts.drivers).length : 0,
        customers: this.data.customers.length
      }
    };
  }
}

// Singleton-Instanz erstellen
const appDataManager = new AppDataManager();

export default appDataManager;
